import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  Plus,
  User,
  Pin,
  PinOff,
  Pencil,
  Trash2,
  Wallet,
  Bot,
  PanelLeftOpen,
  PanelLeftClose,
  Search,
  X,
  LibraryBig,
  LifeBuoy,
  Smartphone,
  Headphones,
  Boxes,
  BookOpen,
  Image as ImageIcon,
} from "lucide-react";
import { Tip } from "@/components/Tip";
import { ProjectsList } from "@/components/projects/ProjectsList";
import type { Project } from "@/lib/api/projects";
import { projectColorHex } from "@/lib/api/projects";
import { cn } from "@/lib/utils";
import type { Chat } from "@/lib/api";

interface SidebarProps {
  onNewThread: () => void;
  chats: Chat[];
  activeChatId: string | null;
  onChatSelect: (id: string) => void;
  onRenameChat: (id: string, title: string) => Promise<void>;
  onTogglePin: (id: string, pinned: boolean) => Promise<void>;
  onDeleteChat: (id: string) => Promise<void>;
  // F5 (decision #10): working profile-menu (replaces fake «pro»).
  tariffCode?: string | null;
  onOpenBilling: () => void;
  onOpenAccount: () => void;
  // F8 (decision #6): Agent — ComingSoon screen.
  onOpenAgents: () => void;
  // File library (50MB per account)
  onOpenLibrary: () => void;
  // I3: Image Generation panel (backend-authorized; feature-gated)
  onOpenImage: () => void;
  onOpenSupport: () => void;
  onOpenMobile: () => void;
  // Documentation (D2): full Documentation view (TechSpec §29).
  onOpenDocs: () => void;
  onLogout: () => void;
  // Display name for the expanded footer (existing auth/user state, never hardcoded).
  userName?: string;
  // Mobile drawer mode (ChatPage top bar): when provided the sidebar renders
  // as a fixed full-screen overlay on < md and closes itself after navigation.
  onNavigate?: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  // Projects (Phase 4): data owned by the parent (single source of truth).
  projects: Project[];
  projectsLoading: boolean;
  projectsError: string | null;
  activeProjectId: string | null;
  onProjectSelect: (id: string) => void;
  onOpenProjects: () => void;
  onProjectCreate: () => void;
  onProjectsRefresh: () => void;
  onProjectArchived: (id: string) => void;
  onProjectRestored: (id: string) => void;
  // Global navigation contract: the CURRENT top-level section, owned by the
  // app shell (ChatPage). Drives the active nav highlight so it always
  // follows the actual destination — never derived from page-local state.
  // 'project' = the Projects experience too (drill-down of 'projects').
  activeSection?: "chat" | "projects" | "project" | "agents" | "images" | "docs";
}

/** In-palette confirmation dialog (replaces the white native confirm()). */
function ConfirmDialog({
  title,
  text,
  confirmLabel = "Удалить",
  onConfirm,
  onCancel,
}: {
  title: string;
  text: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onCancel} aria-hidden />
      <div className="relative w-full max-w-sm rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
        <h3 className="text-base font-medium text-primary">{title}</h3>
        <p className="mt-2 text-sm text-secondary">{text}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={onConfirm}
            className="h-9 px-4 rounded-lg text-sm font-medium bg-red-500/90 text-white hover:bg-red-500 transition-colors flex items-center gap-1.5"
          >
            <Trash2 size={14} /> {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Sidebar — two mutually exclusive views (never stacked side by side):
 *
 * 1. COLLAPSED — the 64px icon rail, exactly as before (width 64px,
 *    40x40 buttons, py-4, gap-4, 20px icons). No chat history in the rail.
 *    `R` on top is the expand control: on hover it shows `R →`.
 *
 * 2. EXPANDED — a single ~280px panel (64px → ~280px). Structural zones
 *    (RAIOS Ch.11.7 sidebar UX contract):
 *      - Header (R / Search / Collapse) — FIXED (flex shrink-0, never scrolls)
 *      - Primary Navigation — FIXED (flex shrink-0, never scrolls)
 *      - Projects List — INDEPENDENT SCROLL (flex:1; min-height:0;
 *        overflow-y:auto) — the only scroll zone in this area
 *      - Chat history — FIXED-height zone with its OWN scroll (flex:1;
 *        min-height:0; overflow-y:auto)
 *      - Footer (Support / User) — FIXED (flex shrink-0, never scrolls)
 *    The whole panel is `display:flex; flex-direction:column; height:100%`
 *    with NO overflow on the container: with 50+ projects only the projects
 *    list scrolls — header, navigation and footer never move.
 */
export function Sidebar({
  onNewThread,
  chats,
  activeChatId,
  onChatSelect,
  onRenameChat,
  onTogglePin,
  onDeleteChat,
  tariffCode,
  onOpenBilling,
  onOpenAccount,
  onOpenAgents,
  onOpenLibrary,
  onOpenImage,
  onOpenSupport,
  onOpenMobile,
  onOpenDocs,
  onLogout,
  userName,
  onNavigate,
  mobileOpen,
  onMobileClose,
  projects,
  projectsLoading,
  projectsError,
  activeProjectId,
  onProjectSelect,
  onOpenProjects,
  onProjectCreate,
  onProjectsRefresh,
  onProjectArchived,
  onProjectRestored,
  activeSection,
}: SidebarProps) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && mobileOpen) onMobileClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [mobileOpen, onMobileClose]);

  // Drawer closes after any navigation action on mobile.
  const nav = useCallback(
    (fn: () => void) => () => {
      fn();
      if (window.innerWidth < 768) onMobileClose();
    },
    [onMobileClose]
  );

  // Active-section highlight (global nav contract): the app shell owns the
  // current destination — the sidebar only reflects it, never re-derives it
  // from page-local state. 'project' drills into the Projects experience.
  const sectionActive = (
    s: "projects" | "agents" | "images" | "docs"
  ) => activeSection === s || (s === "projects" && activeSection === "project");
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // F5: profile-menu open state
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  // Close profile-menu on outside click
  useEffect(() => {
    if (!showProfileMenu) return;
    const handler = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showProfileMenu]);

  // Search by chat title (client-side over the loaded history, limit=100).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) => (c.title || "").toLowerCase().includes(q));
  }, [chats, search]);

  const startEdit = (chat: Chat) => {
    setEditingId(chat.id);
    setEditValue(chat.title || "");
  };

  const commitEdit = async (chatId: string) => {
    const title = editValue.trim();
    setEditingId(null);
    if (title) {
      await onRenameChat(chatId, title);
    }
  };

  const doDelete = async () => {
    if (deleteId) {
      await onDeleteChat(deleteId);
    }
    setDeleteId(null);
  };

  const deleteTarget = deleteId ? chats.find((c) => c.id === deleteId) : null;

  // F5: real tariff label (decision #8: tariff is admin-assigned; null = Free).
  const tariffLabel = tariffCode || "Free";

  // Backend orders pinned-first + updated_at desc (GET /api/chats).
  const pinnedChats = filtered.filter((c) => c.is_pinned);
  const otherChats = filtered.filter((c) => !c.is_pinned);

  const renderItem = (chat: Chat) => (
    <div
      key={chat.id}
      className={cn(
        "group flex items-center gap-2 pl-3 pr-2 py-2 mx-2 rounded-lg cursor-pointer transition-colors",
        activeChatId === chat.id ? "bg-hoverbg2" : "hover:bg-hoverbg"
      )}
      onClick={nav(() => onChatSelect(chat.id))}
    >
      {editingId === chat.id ? (
        <input
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => commitEdit(chat.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitEdit(chat.id);
            if (e.key === "Escape") setEditingId(null);
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 bg-hoverbg border border-hairline rounded px-1.5 py-0.5 text-sm text-primary focus:outline-none focus:border-accent"
        />
      ) : (
        <span className="flex-1 min-w-0 flex items-center gap-1.5">
          {chat.project_id ? (
            <>
              {/* Project identity marker: the project's own icon in its color
                  (same system as the project list — no second identity). */}
              <span
                className="shrink-0 w-4 h-4 flex items-center justify-center text-[13px] leading-none"
                style={{ color: projectColorHex(chat.project_color) }}
                aria-hidden
              >
                {chat.project_icon || "◆"}
              </span>
              <span className="text-sm text-primary truncate">{chat.title || "Без названия"}</span>
              <span
                className="shrink-0 text-[9px] uppercase tracking-wide px-1 rounded border"
                style={{
                  color: projectColorHex(chat.project_color),
                  borderColor: projectColorHex(chat.project_color) + "66",
                }}
              >
                Проект
              </span>
            </>
          ) : (
            <span className="text-sm text-primary truncate">{chat.title || "Без названия"}</span>
          )}
        </span>
      )}
      {chat.is_pinned && editingId !== chat.id && (
        <Pin size={12} className="shrink-0 text-accent" />
      )}
      <div className="flex md:hidden md:group-hover:flex items-center gap-0.5 shrink-0">
        <Tip label="Переименовать" side="top">
          <button
            onClick={(e) => {
              e.stopPropagation();
              startEdit(chat);
            }}
            className="p-1 rounded text-tertiary hover:text-primary transition-colors"
          >
            <Pencil size={13} />
          </button>
        </Tip>
        <Tip label={chat.is_pinned ? "Открепить" : "Закрепить"} side="top">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(chat.id, !chat.is_pinned);
            }}
            className="p-1 rounded text-tertiary hover:text-primary transition-colors"
          >
            {chat.is_pinned ? <PinOff size={13} /> : <Pin size={13} />}
          </button>
        </Tip>
        <Tip label="Удалить" side="top">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteId(chat.id);
            }}
            className="p-1 rounded text-tertiary hover:text-red-400 transition-colors"
          >
            <Trash2 size={13} />
          </button>
        </Tip>
      </div>
    </div>
  );

  return (
    <>
      {/* ===== DESKTOP (md+): COLLAPSED 64px icon rail / EXPANDED 288px panel
          — unchanged design from DEV. Hidden on mobile (drawer instead). ===== */}
      <div className="hidden md:block h-full">
      {/* ===== COLLAPSED: 64px icon rail (unchanged design from DEV) ===== */}
      {!expanded && (
        <div className="w-16 h-full bg-app border-r border-hairline flex flex-col items-center py-4 gap-4 z-50 relative shrink-0">
          {/* Brand mark = expand control: on hover shows `R →` (spec §3).
              The R keeps its position on top; no permanent expand slot. */}
          <Tip label="Развернуть панель" side="right">
            <button
              onClick={() => setExpanded(true)}
              className="w-10 h-10 flex items-center justify-center shrink-0 group/r rounded-lg bg-hoverbg hover:bg-hoverbg2 transition-colors"
              aria-label="Развернуть панель"
            >
              <span className="relative w-10 h-10 flex items-center justify-center">
                <img
                  src="/icons/brand-r-64.png"
                  alt="RealityAI"
                  className="w-8 h-8 select-none group-hover/r:opacity-0 transition-opacity absolute"
                  draggable={false}
                />
                <PanelLeftOpen
                  size={20}
                  className="text-secondary hover:text-primary opacity-0 group-hover/r:opacity-100 transition-opacity"
                />
              </span>
            </button>
          </Tip>

          {/* New chat */}
          <Tip label="Новая задача" side="right">
            <button
              onClick={onNewThread}
              data-testid="nav-new-chat"
              className="w-10 h-10 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors shrink-0"
            >
              <Plus size={20} />
            </button>
          </Tip>

          {/* Projects (Phase 4) */}
          <Tip label="Проекты" side="right">
            <button
              onClick={onOpenProjects}
              data-testid="nav-projects"
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                sectionActive("projects")
                  ? "bg-hoverbg2 text-accent"
                  : "bg-hoverbg hover:bg-hoverbg2 text-secondary hover:text-primary"
              )}
            >
              <Boxes size={20} />
            </button>
          </Tip>

          {/* Библиотека — file library (50MB per account) */}
          <Tip label="Библиотека" side="right">
            <button
              onClick={onOpenLibrary}
              className="w-10 h-10 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
            >
              <LibraryBig size={20} />
            </button>
          </Tip>

          {/* I3: Image Generation (backend-authorized, feature-gated) */}
          <Tip label="Изображения" side="right">
            <button
              onClick={onOpenImage}
              data-testid="nav-image"
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center transition-colors",
                sectionActive("images")
                  ? "bg-hoverbg2 text-accent"
                  : "bg-hoverbg hover:bg-hoverbg2 text-secondary hover:text-primary"
              )}
            >
              <ImageIcon size={20} strokeWidth={1.8} />
            </button>
          </Tip>

          {/* K7: Agent surface (real UI; disabled state handled inside) */}
          <Tip label="Агенты" side="right">
            <button
              onClick={onOpenAgents}
              data-testid="nav-agents"
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center transition-colors shrink-0",
                sectionActive("agents")
                  ? "bg-hoverbg2 text-accent"
                  : "bg-hoverbg hover:bg-hoverbg2 text-secondary hover:text-primary"
              )}
            >
              <Bot size={20} />
            </button>
          </Tip>

          {/* Documentation (D2) — full Documentation view */}
          <Tip label="Документация" side="right">
            <button
              onClick={onOpenDocs}
              data-testid="nav-docs"
              className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center transition-colors shrink-0",
                sectionActive("docs")
                  ? "bg-hoverbg2 text-accent"
                  : "bg-hoverbg hover:bg-hoverbg2 text-secondary hover:text-primary"
              )}
            >
              <BookOpen size={20} />
            </button>
          </Tip>

          <div className="flex-1" />

          {/* Mobile app (Android/iOS download dialog) — above tech support */}
          <Tip label="Мобильное приложение" side="right">
            <button
              onClick={onOpenMobile}
              className="w-10 h-10 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors shrink-0"
            >
              <Smartphone size={20} className="text-accent" strokeWidth={1.8} />
            </button>
          </Tip>

          {/* Tech support (replaces the theme switcher; theme now lives in Account settings only) */}
          <Tip label="Техподдержка" side="right">
            <button
              onClick={onOpenSupport}
              className="w-10 h-10 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors shrink-0"
            >
              <Headphones size={20} className="text-accent" strokeWidth={1.8} />
            </button>
          </Tip>

          {/* Bottom: profile-menu (F5, replaces fake «pro» div) */}
          <div className="relative mb-2" ref={profileMenuRef}>
            <Tip label="Профиль" side="right">
            <button
              onClick={() => setShowProfileMenu((v) => !v)}
              className="group/prof flex flex-col items-center gap-1"
              aria-label={`Аккаунт — ${tariffLabel}`}
            >
              <div className="w-8 h-8 rounded-full bg-hoverbg2 group-hover/prof:bg-hoverbg flex items-center justify-center text-accent transition-colors">
                <User size={16} strokeWidth={1.8} />
              </div>
              <span className="text-[9px] font-medium text-accent">{tariffLabel}</span>
            </button>
            </Tip>

            {showProfileMenu && (
              <div className="absolute bottom-full left-0 mb-2 w-56 bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-30">
                <MenuItem
                  icon={<User size={15} />}
                  label="Настройки"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onOpenAccount();
                  }}
                />
                <MenuItem
                  icon={<LibraryBig size={15} />}
                  label="Библиотека"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onOpenLibrary();
                  }}
                />
                <MenuItem
                  icon={<Smartphone size={15} />}
                  label="Мобильное приложение"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onOpenMobile();
                  }}
                />
                <MenuItem
                  icon={<Wallet size={15} />}
                  label="Финансы"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onOpenBilling();
                  }}
                />
                <MenuItem
                  icon={<LifeBuoy size={15} />}
                  label="Поддержка"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onOpenSupport();
                  }}
                />
                <MenuItem
                  icon={<Bot size={15} />}
                  label="Агенты"
                  onClick={() => {
                    setShowProfileMenu(false);
                    onOpenAgents();
                  }}
                />
                <div className="border-t border-hairline" />
                <MenuItem
                  icon={<span className="text-xs">⎋</span>}
                  label="Выйти"
                  danger
                  onClick={() => {
                    setShowProfileMenu(false);
                    onLogout();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== EXPANDED: structural flex zones (RAIOS Ch.11.7):
          header FIXED → navigation FIXED → Projects (content-driven,
          max ≈ 10 rows, own scroll after max) → History (remaining
          space, own scroll) → footer FIXED.
          No overflow on the container: 50+ projects scroll ONLY the list. */}
      {expanded && (
        <div className="w-72 h-full bg-app border-r border-hairline z-40 shrink-0">
          <div className="h-full flex flex-col min-w-0">
            {/* Header — R + Search + Collapse. FIXED: never scrolls. */}
            <div className="shrink-0 px-3 pt-4">
              <div className="flex items-center gap-1">
                <img
                  src="/icons/brand-r-64.png"
                  alt="RealityAI"
                  className="w-8 h-8 select-none pl-0.5 pr-1"
                  draggable={false}
                />
                <div className="flex-1" />
                <Tip label="Поиск по чатам" side="bottom">
                  <button
                    onClick={() => setSearchOpen((v) => !v)}
                    className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
                    aria-label="Поиск по чатам"
                  >
                    <Search size={18} />
                  </button>
                </Tip>
                <Tip label="Свернуть панель" side="bottom">
                  <button
                    onClick={() => setExpanded(false)}
                    className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
                    aria-label="Свернуть панель"
                  >
                    <PanelLeftClose size={18} />
                  </button>
                </Tip>
              </div>
              {/* Search input — toggled by the Search button (fixed zone) */}
              {searchOpen && (
                <div className="pt-2">
                  <div className="relative">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary pointer-events-none"
                    />
                    <input
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Поиск по чатам…"
                      className="chat-search-input w-full h-9 pl-8 pr-8 text-sm"
                    />
                    {search && (
                      <button
                        onClick={() => {
                          setSearch("");
                          setSearchOpen(false);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-tertiary hover:text-primary transition-colors"
                        aria-label="Очистить"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Primary navigation — FIXED: never scrolls. */}
            <div className="px-3 pt-4 space-y-4 shrink-0">
              <NavRow
                icon={<Plus size={20} />}
                label="Новая задача"
                onClick={onNewThread}
                testId="nav-new-chat"
              />
              <NavRow
                icon={<Boxes size={20} />}
                label="Проекты"
                onClick={onOpenProjects}
                active={sectionActive("projects")}
                testId="nav-projects"
              />
              <NavRow
                icon={<LibraryBig size={20} />}
                label="Библиотека"
                onClick={onOpenLibrary}
              />
              <NavRow
                icon={<ImageIcon size={20} strokeWidth={1.8} />}
                label="Изображения"
                onClick={onOpenImage}
                active={sectionActive("images")}
                testId="nav-image"
              />
              <NavRow
                icon={<Bot size={20} />}
                label="Агенты"
                onClick={onOpenAgents}
                active={sectionActive("agents")}
                testId="nav-agents"
              />
              <NavRow
                icon={<BookOpen size={20} />}
                label="Документация"
                onClick={onOpenDocs}
                active={sectionActive("docs")}
                testId="nav-docs"
              />
            </div>

            {/* Projects List — the ONLY independent-scroll zone of the
                sidebar (flex:1; min-height:0; overflow-y:auto inside).
                With 50+ projects header/nav/footer stay put. */}
            <ProjectsList
              projects={projects}
              loading={projectsLoading}
              error={projectsError}
              activeProjectId={activeProjectId}
              activeChatId={activeChatId}
              onProjectSelect={(id) => nav(() => onProjectSelect(id))()}
              onCreateClick={onProjectCreate}
              onRefresh={onProjectsRefresh}
              onArchived={onProjectArchived}
              onRestored={onProjectRestored}
            />

            {/* Chat history — takes ALL remaining vertical space (flex:1;
                min-height:0) with its OWN scroll. Projects above it are
                content-driven (max ≈ 10 rows, then their list scrolls) so
                with 0 projects history gets the dominant area. */}
            <div className="min-h-0 flex flex-col" style={{ flex: "1 1 0%" }}>
              <div className="px-4 pt-3 pb-1 text-[11px] font-semibold text-tertiary uppercase tracking-wider shrink-0">
                История
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-1">
                {chats.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-tertiary text-center">Чатов пока нет</div>
                ) : filtered.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-tertiary text-center">
                    По запросу «{search.trim()}» ничего не найдено
                  </div>
                ) : (
                  <>
                    {pinnedChats.length > 0 && (
                      <div className="px-4 pt-1 pb-1 text-[11px] font-semibold text-tertiary uppercase tracking-wider flex items-center gap-1.5">
                        <Pin size={11} /> Закреплённые
                      </div>
                    )}
                    {pinnedChats.map(renderItem)}
                    {pinnedChats.length > 0 && otherChats.length > 0 && (
                      <div className="px-4 pt-3 pb-1 text-[11px] font-semibold text-tertiary uppercase tracking-wider">
                        Обычные
                      </div>
                    )}
                    {otherChats.map(renderItem)}
                  </>
                )}
              </div>
            </div>

            {/* Footer — Support + User/tariff. Pinned at the bottom (same
                slot as in the rail): does not scroll with the history. */}
            <div className="px-3 pb-4 pt-3 shrink-0">
              <button
                onClick={onOpenMobile}
                className="w-full flex items-center gap-3 pl-3 pr-3 py-2 rounded-lg text-sm text-secondary hover:bg-hoverbg hover:text-primary transition-colors"
              >
                <Smartphone size={20} className="text-accent shrink-0" strokeWidth={1.8} />
                Мобильное приложение
              </button>

              <button
                onClick={onOpenSupport}
                className="w-full flex items-center gap-3 pl-3 pr-3 py-2 rounded-lg text-sm text-secondary hover:bg-hoverbg hover:text-primary transition-colors"
              >
                <Headphones size={20} className="text-accent shrink-0" strokeWidth={1.8} />
                Тех. поддержка
              </button>

              <div className="relative mt-1" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu((v) => !v)}
                  className="w-full flex items-center gap-3 pl-2 pr-3 py-2 rounded-lg text-left hover:bg-hoverbg transition-colors"
                  aria-label={`Аккаунт — ${tariffLabel}`}
                >
                  <div className="w-8 h-8 rounded-full bg-hoverbg2 flex items-center justify-center text-accent shrink-0">
                    <User size={16} strokeWidth={1.8} />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-primary truncate">
                      {userName || "Пользователь"}
                    </span>
                    <span className="text-[11px] font-medium text-accent">{tariffLabel}</span>
                  </div>
                </button>

                {showProfileMenu && (
                  <div className="absolute bottom-full left-0 mb-2 w-56 bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-30">
                    <MenuItem
                      icon={<User size={15} />}
                      label="Настройки"
                      onClick={() => {
                        setShowProfileMenu(false);
                        onOpenAccount();
                      }}
                    />
                    <MenuItem
                      icon={<LibraryBig size={15} />}
                      label="Библиотека"
                      onClick={() => {
                        setShowProfileMenu(false);
                        onOpenLibrary();
                      }}
                    />
                    <MenuItem
                      icon={<Wallet size={15} />}
                      label="Финансы"
                      onClick={() => {
                        setShowProfileMenu(false);
                        onOpenBilling();
                      }}
                    />
                    <MenuItem
                      icon={<LifeBuoy size={15} />}
                      label="Поддержка"
                      onClick={() => {
                        setShowProfileMenu(false);
                        onOpenSupport();
                      }}
                    />
                    <MenuItem
                      icon={<Bot size={15} />}
                      label="Агенты"
                      onClick={() => {
                        setShowProfileMenu(false);
                        onOpenAgents();
                      }}
                    />
                    <div className="border-t border-hairline" />
                    <MenuItem
                      icon={<span className="text-xs">⎋</span>}
                      label="Выйти"
                      danger
                      onClick={() => {
                        setShowProfileMenu(false);
                        onLogout();
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>{/* /desktop (hidden md:block) */}

      {/* ===== MOBILE (< md): full-screen drawer, driven by ChatPage top bar ===== */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onMobileClose} aria-hidden />
          <div className="absolute inset-y-0 left-0 w-full bg-app flex flex-col min-w-0">
            {/* Header — R + Search + Close */}
            <div className="flex items-center gap-1 px-3 pt-4 pb-2 shrink-0">
              <img
                src="/icons/brand-r-64.png"
                alt="RealityAI"
                className="w-8 h-8 select-none pl-0.5 pr-1"
                draggable={false}
              />
              <span className="text-sm font-medium text-primary">RealityAI</span>
              <div className="flex-1" />
              <button
                onClick={() => setSearchOpen((v) => !v)}
                className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
                aria-label="Поиск по чатам"
              >
                <Search size={18} />
              </button>
              <button
                onClick={onMobileClose}
                className="w-9 h-9 rounded-lg bg-hoverbg hover:bg-hoverbg2 flex items-center justify-center text-secondary hover:text-primary transition-colors"
                aria-label="Закрыть меню"
              >
                <X size={18} />
              </button>
            </div>

            {searchOpen && (
              <div className="px-3 pb-1 shrink-0">
                <div className="relative">
                  <Search
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary pointer-events-none"
                  />
                  <input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Поиск по чатам…"
                    className="chat-search-input w-full h-9 pl-8 pr-8 text-sm"
                  />
                  {search && (
                    <button
                      onClick={() => {
                        setSearch("");
                        setSearchOpen(false);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-tertiary hover:text-primary transition-colors"
                      aria-label="Очистить"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Primary navigation */}
            <div className="px-3 pt-3 space-y-4 shrink-0">
              <NavRow icon={<Plus size={20} />} label="Новая задача" onClick={nav(onNewThread)} />
              <NavRow
                icon={<Boxes size={20} />}
                label="Проекты"
                onClick={nav(onOpenProjects)}
                active={sectionActive("projects")}
              />
              <NavRow
                icon={<LibraryBig size={20} />}
                label="Библиотека"
                onClick={nav(onOpenLibrary)}
              />
              <NavRow
                icon={<ImageIcon size={20} strokeWidth={1.8} />}
                label="Изображения"
                onClick={nav(onOpenImage)}
                active={sectionActive("images")}
              />
              <NavRow
                icon={<Bot size={20} />}
                label="Агенты"
                onClick={nav(onOpenAgents)}
                active={sectionActive("agents")}
              />
              <NavRow
                icon={<BookOpen size={20} />}
                label="Документация"
                onClick={nav(onOpenDocs)}
                active={sectionActive("docs")}
              />
            </div>

            {/* Projects List — independent scroll zone (same contract as
                desktop): header/nav/footer stay fixed. */}
            <ProjectsList
              projects={projects}
              loading={projectsLoading}
              error={projectsError}
              activeProjectId={activeProjectId}
              activeChatId={activeChatId}
              onProjectSelect={(id) => nav(() => onProjectSelect(id))()}
              onCreateClick={onProjectCreate}
              onRefresh={onProjectsRefresh}
              onArchived={onProjectArchived}
              onRestored={onProjectRestored}
            />

            {/* Chat history — remaining space with its own scroll */}
            <div className="min-h-0 flex flex-col" style={{ flex: "1 1 0%" }}>
              <div className="px-4 pt-3 pb-1 text-[11px] font-semibold text-tertiary uppercase tracking-wider shrink-0">
                История
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-2 pb-2">
              {chats.length === 0 ? (
                <div className="px-4 py-6 text-sm text-tertiary text-center">Чатов пока нет</div>
              ) : filtered.length === 0 ? (
                <div className="px-4 py-6 text-sm text-tertiary text-center">
                  По запросу «{search.trim()}» ничего не найдено
                </div>
              ) : (
                <>
                  {pinnedChats.length > 0 && (
                    <div className="px-4 pt-2 pb-1 text-[11px] font-semibold text-tertiary uppercase tracking-wider flex items-center gap-1.5">
                      <Pin size={11} /> Закреплённые
                    </div>
                  )}
                  {pinnedChats.map(renderItem)}
                  {otherChats.length > 0 && (
                    <div className="px-4 pt-4 pb-1 text-[11px] font-semibold text-tertiary uppercase tracking-wider">
                      История
                    </div>
                  )}
                  {otherChats.map(renderItem)}
                </>
              )}
              </div>
            </div>

            {/* Footer — Support + User/tariff, pinned at the bottom */}
            <div className="px-3 pb-4 pt-3 shrink-0">
              <button
                onClick={nav(onOpenSupport)}
                className="w-full flex items-center gap-3 pl-3 pr-3 py-2 rounded-lg text-sm text-secondary hover:bg-hoverbg hover:text-primary transition-colors"
              >
                <Headphones size={20} className="text-accent shrink-0" strokeWidth={1.8} />
                Тех. поддержка
              </button>

              <div className="relative mt-1" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu((v) => !v)}
                  className="w-full flex items-center gap-3 pl-2 pr-3 py-2 rounded-lg text-left hover:bg-hoverbg transition-colors"
                  aria-label={`Аккаунт — ${tariffLabel}`}
                >
                  <div className="w-8 h-8 rounded-full bg-hoverbg2 flex items-center justify-center text-accent shrink-0">
                    <User size={16} strokeWidth={1.8} />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-primary truncate">
                      {userName || "Пользователь"}
                    </span>
                    <span className="text-[11px] font-medium text-accent">{tariffLabel}</span>
                  </div>
                </button>

                {showProfileMenu && (
                  <div className="absolute bottom-full left-0 mb-2 w-56 bg-elevated border border-strongline rounded-lg shadow-lg overflow-hidden z-30">
                    <MenuItem
                      icon={<User size={15} />}
                      label="Настройки"
                      onClick={() => {
                        setShowProfileMenu(false);
                        nav(onOpenAccount)();
                      }}
                    />
                    <MenuItem
                      icon={<LibraryBig size={15} />}
                      label="Библиотека"
                      onClick={() => {
                        setShowProfileMenu(false);
                        nav(onOpenLibrary)();
                      }}
                    />
                    <MenuItem
                      icon={<Wallet size={15} />}
                      label="Финансы"
                      onClick={() => {
                        setShowProfileMenu(false);
                        nav(onOpenBilling)();
                      }}
                    />
                    <MenuItem
                      icon={<LifeBuoy size={15} />}
                      label="Поддержка"
                      onClick={() => {
                        setShowProfileMenu(false);
                        nav(onOpenSupport)();
                      }}
                    />
                    <MenuItem
                      icon={<Bot size={15} />}
                      label="Агенты"
                      onClick={() => {
                        setShowProfileMenu(false);
                        nav(onOpenAgents)();
                      }}
                    />
                    <div className="border-t border-hairline" />
                    <MenuItem
                      icon={<span className="text-xs">⎋</span>}
                      label="Выйти"
                      danger
                      onClick={() => {
                        setShowProfileMenu(false);
                        onLogout();
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* In-palette delete confirmation (never the white native dialog) */}
      {deleteTarget && (
        <ConfirmDialog
          title="Удалить чат?"
          text={`«${deleteTarget.title || "Без названия"}» будет удалён без возможности восстановления.`}
          onConfirm={doDelete}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </>
  );
}

/** Expanded nav row: icon + label (40px tall, same rhythm as the rail). */
function NavRow({
  icon,
  label,
  onClick,
  active,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  testId?: string;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "w-full h-10 flex items-center gap-3 pl-3 pr-3 rounded-lg text-sm transition-colors",
        active
          ? "bg-hoverbg2 text-accent"
          : "text-secondary hover:bg-hoverbg hover:text-primary"
      )}
    >
      <span className="shrink-0 flex items-center justify-center w-5 h-5">{icon}</span>
      {label}
    </button>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors",
        disabled
          ? "text-disabled cursor-not-allowed"
          : danger
            ? "text-red-400 hover:bg-red-500/10"
            : "text-primary hover:bg-hoverbg"
      )}
    >
      <span className="shrink-0">{icon}</span>
      {label}
    </button>
  );
}
